import {
    createContext,
    Dispatch,
    PropsWithChildren,
    SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useState
} from "react";

import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import {
    getIdentityInterstitialUrl,
    ProductKey
} from "@/components/signup/utils";
import {
    trackContinueWithSocialSignups,
    trackDataFoundationsAccountCreationComponent,
    trackFormSubmit,
    trackInvalidEmail,
    trackRecaptchaFailure,
    trackSignFormLoad,
    trackSignupFailure,
    trackSubmitButtonClick,
    trackSubmitSuccess,
    trackTimeToFormReady,
    getSocialButtonUrl,
    UfoCustomTimingKey,
    ufoEventAccountCreation,
    ufoEventSignupFormLoad,
    getNameYourSiteRedirectUrl,
    getContinueParam,
    getLocalePath,
    getMethodsAvailableRedirection,
    getRecaptcha
} from "@/components/signup/utils";
import TryIntent from "@/components/try-intent/src";
import { type Product, useUserContext } from "@/components/user-context";
import { useBxpWebStargateClient } from "@/libraries/bxp-web-stargate-client/src/index";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";
import { useLocation } from "@/utils/location-provider";

import { useAnalyticsClient } from "../../../../analytics/wac-analytics/analytics-client";
export interface AccountCreationContext {
    dataFoundationsActionSubjectId: "embedded" | "standalone";
    edition?: string;
    formError: boolean;
    hasSubmitted: boolean;
    identityCobranding: string;
    isReadyForInvalid: boolean;
    isSubmitting: boolean;
    onClickSocialButton: (provider: string) => void;
    onClickSubmitButton: () => void;
    onSubmitWithReCAPTCHA: ({ email }: { email: string }) => Promise<void>;
    product?: Product;
    redirectTo: string;
    setIsEmailValid: Dispatch<SetStateAction<boolean>>;
    setIsReadyForInvalid: Dispatch<SetStateAction<boolean>>;
    setTypeAheadEmail: Dispatch<SetStateAction<string>>;
    typeAheadEmail: string;
    tryIntentAnalyticsEventHasFired: boolean;
}

const { IDENTITY_USER_SIGNUP_API } = UfoCustomTimingKey;

export const AccountCreationContext =
    createContext<AccountCreationContext | null>(null);

export const useAccountCreationContext = (): AccountCreationContext => {
    const context = useContext(AccountCreationContext);
    if (!context) {
        throw new Error(
            "useAccountCreationContext must be used within a AccountCreationContextProvider"
        );
    }
    return context;
};

export const AccountCreationContextProvider = ({
    continueUrl,
    identityCobranding = "wac",
    templateId,
    children
}: PropsWithChildren<{
    continueUrl?: string;
    identityCobranding?: string;
    templateId?: string;
    // eslint-disable-next-line complexity
}>) => {
    const location = useLocation();
    const isEmbedded = !location.pathname.endsWith("/try/cloud/signup");
    const { product, edition } = useUserContext();
    const analyticsClient = useAnalyticsClient();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const intl = useIntl();
    const [isEmailValid, setIsEmailValid] = useState(false);
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const [isReadyForInvalid, setIsReadyForInvalid] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formError, setFormError] = useState(false);
    const [_email, setEmail] = useState("");
    const [typeAheadEmail, setTypeAheadEmail] = useState("");
    const [onSubmitProps, setOnSubmitProps] = useState<
        { email: string } | undefined
    >(undefined);
    const [recaptchaFailedToLoad, setRecaptchaFailedToLoad] =
        useState<boolean>(false);
    const [
        recaptchaRetryTimeRemainingSecond,
        setRecaptchaRetryTimeRemainingSecond
    ] = useState(60);
    const [
        tryIntentAnalyticsEventHasFired,
        setTryIntentAnalyticsEventHasFired
    ] = useState(false);
    const localePath = getLocalePath(intl.locale);
    const signupClient = useBxpWebStargateClient();
    const { executeRecaptcha } = useGoogleReCaptcha();

    const continueParam = getContinueParam(location);

    // identity interstitial redirect experiments
    const interstitialRedirectEnabled = checkFeatureFlagGate(
        featureFlags.signupAccountCreationInterstitial
    );

    const dataFoundationsActionSubjectId = !!isEmbedded
        ? "embedded"
        : "standalone";

    // default experiment params
    if (product && identityCobranding === "wac") {
        if (product.name === "confluence") {
            identityCobranding = "wac--otp-confluence";
        } else if (product.name === "jira") {
            identityCobranding = "wac--otp-jira";
        } else if (product.name === "loom") {
            identityCobranding = "wac--loom";
        } else if (product.name === "jira-product-discovery") {
            identityCobranding = "wac--jpd";
        } else if (product.name === "jira-servicedesk") {
            identityCobranding = "wac--jsm";
        } else if (product.name === "service-collection") {
            identityCobranding = "wac--service-collection";
        } else if (product.name === "teamwork-collection") {
            identityCobranding = "wac--teamwork-collection";
        }
    }

    const redirectTo = continueUrl
        ? continueUrl
        : continueParam
        ? continueParam
        : getNameYourSiteRedirectUrl({
              bundle: product?.bundle,
              edition,
              localePath,
              templateId,
              application: identityCobranding
          });

    useEffect(() => {
        if (!analyticsClient) return;

        trackDataFoundationsAccountCreationComponent(createAnalyticsEvent, {
            actionSubjectId: dataFoundationsActionSubjectId,
            productKey: product?.key,
            edition
        });

        ufoEventSignupFormLoad.success({
            customData: {
                webPlatform: "bxp-ui",
                reactComponent: "DefaultSignupForm",
                isLoggedIn: false
            }
        });

        trackSignFormLoad(createAnalyticsEvent);
        trackTimeToFormReady(createAnalyticsEvent, performance.now());
    }, [
        createAnalyticsEvent,
        edition,
        product?.key,
        dataFoundationsActionSubjectId,
        analyticsClient
    ]);

    const onSubmitWithReCAPTCHA = useCallback(
        // eslint-disable-next-line complexity
        async ({ email }: { readonly email: string }) => {
            setIsReadyForInvalid(true);
            if (!email || !isEmailValid) {
                trackInvalidEmail(createAnalyticsEvent);
                return;
            }

            trackFormSubmit(createAnalyticsEvent, product?.key, edition);

            try {
                // check identity interstitial redirect experiments for enrollment
                if (interstitialRedirectEnabled) {
                    const url = getIdentityInterstitialUrl(
                        email,
                        redirectTo,
                        identityCobranding
                    );
                    createAnalyticsEvent({
                        eventType: "ui",
                        action: "succeeded",
                        actionSubject: "accountCreationInterstitialRedirect",
                        containerId: "bxp-ui-signup",
                        attributes: {
                            productKey: product?.key,
                            edition
                        }
                    }).fire();
                    window.location.assign(url);
                    return;
                }
            } catch (error) {
                createAnalyticsEvent({
                    eventType: "ui",
                    action: "errored",
                    actionSubject: "accountCreationInterstitialRedirect",
                    containerId: "bxp-ui-signup",
                    attributes: {
                        productKey: product?.key,
                        edition
                    }
                }).fire();
            }

            setOnSubmitProps({ email });
            setIsSubmitting(true);
            if (!executeRecaptcha && recaptchaRetryTimeRemainingSecond > 0) {
                // set trigger that will wait for Recaptcha retry logic
                setFormError(true);
                setIsSubmitting(false);
                setRecaptchaFailedToLoad(true);
                return;
            }
            setRecaptchaFailedToLoad(false);

            // Start UFO to begin duration measurements for account creation
            ufoEventAccountCreation.start({
                productKey: product?.key,
                webPlatform: "bxp-ui"
            });

            const gRecaptchaResponse = await getRecaptcha(
                email,
                executeRecaptcha
            );

            ufoEventAccountCreation.setCustomData({
                gRecaptchaResponse: gRecaptchaResponse ?? "none",
                executeRecaptchaExists: Boolean(executeRecaptcha),
                reactComponent: "AccountCreationContextProvider"
            });

            // Handle missing reCAPTCHA response
            if (!gRecaptchaResponse) {
                trackRecaptchaFailure(createAnalyticsEvent, "account-creation");
                const invalidRecaptchaError = new Error(
                    "Missing reCAPTCHA token"
                );
                ufoEventAccountCreation.failure({
                    customData: {
                        reactComponent: "AccountCreationContextProvider"
                    },
                    error: invalidRecaptchaError
                });
                throw invalidRecaptchaError;
            }

            const existingUserRedirectUrl =
                await getMethodsAvailableRedirection({
                    email,
                    application: identityCobranding,
                    signupClient,
                    createAnalyticsEvent,
                    successRedirect: redirectTo
                }).catch((error) => {
                    setFormError(true);
                    setIsSubmitting(false);
                    trackSignupFailure(createAnalyticsEvent);
                    throw new Error(error);
                });
            if (existingUserRedirectUrl) {
                window.location.assign(
                    existingUserRedirectUrl.replace("/initiate/password", "")
                );
                return;
            }

            const newUserRedirectTo = redirectTo + `&n2n=true`;

            ufoEventAccountCreation.startSubstep(IDENTITY_USER_SIGNUP_API);

            await signupClient
                .userSignup({
                    application: identityCobranding,
                    displayName: "",
                    email,
                    gRecaptchaResponse,
                    locale: intl.locale,
                    products: [
                        {
                            productKey:
                                product?.key || ProductKey.JIRA_SOFTWARE,
                            edition: edition || "free"
                        }
                    ],
                    redirectTo: newUserRedirectTo
                })
                .then(({ otpRedirect }: { otpRedirect?: string }) => {
                    ufoEventAccountCreation.endSubstep(
                        IDENTITY_USER_SIGNUP_API
                    );

                    if (!otpRedirect) {
                        throw new Error("No OTP redirect");
                    }

                    // Reliability SLO "good event" metric emitted for submit account creation
                    ufoEventAccountCreation.success({
                        customData: {
                            reactComponent: "AccountCreationContextProvider"
                        }
                    });

                    trackSubmitSuccess(createAnalyticsEvent);

                    // Paid landing Jira AI experiment: tag OTP redirects when CGS key is present.
                    if (templateId === "cfind-4666-jira-ai-preview") {
                        const cgsKey =
                            typeof window !== "undefined"
                                ? new URL(
                                      window.location.href
                                  ).searchParams.get("cgsKey") ?? undefined
                                : undefined;
                        if (cgsKey) {
                            createAnalyticsEvent({
                                eventType: "track",
                                action: "succeeded",
                                actionSubject: "paidLandingJiraAiExperiment",
                                attributes: {
                                    step: "OtpRedirected",
                                    flow: "paidLandingJiraAi",
                                    experimentKey: "cfind4666JiraFfl",
                                    templateId,
                                    cgsKey
                                }
                            }).fire();
                        }
                    }

                    window.location.assign(otpRedirect);
                })
                .catch((error: string) => {
                    // Reliability SLO "bad event" metric emitted for submit account creation
                    ufoEventAccountCreation.endSubstep(
                        IDENTITY_USER_SIGNUP_API
                    );
                    ufoEventAccountCreation.failure({
                        customData: {
                            reactComponent: "AccountCreationContextProvider"
                        },
                        error
                    });

                    setEmail(email);
                    setFormError(true);
                    setHasSubmitted(true);
                    setIsSubmitting(false);
                    trackSignupFailure(createAnalyticsEvent);
                    throw new Error(error);
                });
        },
        [
            createAnalyticsEvent,
            edition,
            executeRecaptcha,
            identityCobranding,
            interstitialRedirectEnabled,
            intl.locale,
            isEmailValid,
            product,
            recaptchaRetryTimeRemainingSecond,
            redirectTo,
            signupClient
        ]
    );

    useEffect(() => {
        if (recaptchaFailedToLoad && recaptchaRetryTimeRemainingSecond <= 0) {
            ufoEventAccountCreation.failure({
                customData: {
                    reactComponent: "AccountCreationContextProvider",
                    recaptchaRetryTimeRemainingSecond: 0
                },
                error: new Error("Recaptcha retry failed")
            });
        }
        if (onSubmitProps && recaptchaFailedToLoad && executeRecaptcha) {
            onSubmitWithReCAPTCHA(onSubmitProps)
                .then(() => {
                    ufoEventAccountCreation.success({
                        customData: {
                            reactComponent: "AccountCreationContextProvider",
                            analyticsEvent: "succeeded",
                            recaptchaRetryTimeRemainingSecond
                        }
                    });
                })
                .catch((error) => {
                    setFormError(true);
                    ufoEventAccountCreation.failure({
                        customData: {
                            reactComponent: "AccountCreationContextProvider",
                            analyticsEvent: "errored",
                            recaptchaRetryTimeRemainingSecond
                        },
                        error
                    });
                    return;
                })
                .finally(() => {
                    setIsSubmitting(false);
                });
        }
    }, [
        executeRecaptcha,
        recaptchaFailedToLoad,
        onSubmitProps,
        onSubmitWithReCAPTCHA,
        recaptchaRetryTimeRemainingSecond
    ]);

    useEffect(() => {
        if (!recaptchaFailedToLoad) return;
        if (recaptchaRetryTimeRemainingSecond <= 0) return;
        const intervalId = setInterval(() => {
            setRecaptchaRetryTimeRemainingSecond(
                recaptchaRetryTimeRemainingSecond - 1
            );
        }, 1000);
        return () => clearInterval(intervalId);
    }, [recaptchaFailedToLoad, recaptchaRetryTimeRemainingSecond]);

    const onClickSocialButton = (provider: string) => {
        trackContinueWithSocialSignups(
            createAnalyticsEvent,
            provider,
            product?.key,
            edition
        );
        const socialRedirectUrl = getSocialButtonUrl({
            redirectTo,
            identityCobranding,
            provider
        });
        window.location.assign(socialRedirectUrl);
    };

    const onClickSubmitButton = () =>
        trackSubmitButtonClick(createAnalyticsEvent);

    return (
        <AccountCreationContext.Provider
            value={{
                dataFoundationsActionSubjectId,
                edition,
                formError,
                hasSubmitted,
                identityCobranding,
                isReadyForInvalid,
                isSubmitting,
                onClickSocialButton,
                onClickSubmitButton,
                onSubmitWithReCAPTCHA,
                product,
                redirectTo,
                setIsEmailValid,
                setIsReadyForInvalid,
                setTypeAheadEmail,
                typeAheadEmail,
                tryIntentAnalyticsEventHasFired
            }}
        >
            <TryIntent
                edition={edition}
                intentType="form"
                productKey={product?.key}
                onAnalyticsEventFired={() =>
                    !tryIntentAnalyticsEventHasFired &&
                    setTryIntentAnalyticsEventHasFired(true)
                }
            >
                {children}
            </TryIntent>
        </AccountCreationContext.Provider>
    );
};
